import React, { useState, useEffect } from 'react'
import { Bell, Mail, Settings, Save } from 'lucide-react'
import { toast } from 'react-toastify'
import { apiService } from '../../services/api'

interface ReminderPreferences {
  emailReminders: boolean
  smsReminders: boolean
  reminderFrequency: 'DAILY' | 'WEEKLY' | 'MONTHLY'
  escalationEnabled: boolean
}

interface ReminderPreferencesProps {
  isOpen: boolean
  onClose: () => void
}

const ReminderPreferences: React.FC<ReminderPreferencesProps> = ({ isOpen, onClose }) => {
  const [preferences, setPreferences] = useState<ReminderPreferences>({
    emailReminders: true,
    smsReminders: false,
    reminderFrequency: 'DAILY',
    escalationEnabled: true
  })
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadPreferences()
    }
  }, [isOpen])

  const loadPreferences = async () => {
    try {
      setLoading(true)
      const response = await apiService.get('/api/payment/reminder-preferences')

      // Ensure we have valid data structure
      const responseData = (response as any).data?.data
      if (responseData && typeof responseData === 'object') {
        setPreferences({
          emailReminders: responseData.emailReminders ?? true,
          smsReminders: responseData.smsReminders ?? false,
          reminderFrequency: responseData.reminderFrequency ?? 'DAILY',
          escalationEnabled: responseData.escalationEnabled ?? true
        })
      } else {
        // Use default preferences if response is malformed
        setPreferences({
          emailReminders: true,
          smsReminders: false,
          reminderFrequency: 'DAILY',
          escalationEnabled: true
        })
      }
    } catch (error: any) {
      console.error('Error loading preferences:', error)
      toast.error('Failed to load reminder preferences')

      // Set default preferences on error
      setPreferences({
        emailReminders: true,
        smsReminders: false,
        reminderFrequency: 'DAILY',
        escalationEnabled: true
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      await apiService.put('/api/payment/reminder-preferences', preferences)
      toast.success('Reminder preferences saved successfully')
      onClose()
    } catch (error: any) {
      console.error('Error saving preferences:', error)
      toast.error(error.response?.data?.message || 'Failed to save preferences')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center space-x-2">
            <Settings className="w-6 h-6 text-gray-600" />
            <h2 className="text-xl font-semibold">Reminder Preferences</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">×</button>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading preferences...</p>
          </div>
        ) : preferences ? (
          <div className="space-y-4">
            <div>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={preferences.emailReminders}
                  onChange={(e) => setPreferences({
                    ...preferences,
                    emailReminders: e.target.checked
                  })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <Mail className="w-5 h-5 text-gray-600" />
                <span>Email Reminders</span>
              </label>
            </div>

            <div>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={preferences.smsReminders}
                  onChange={(e) => setPreferences({
                    ...preferences,
                    smsReminders: e.target.checked
                  })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>SMS Reminders (Coming Soon)</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reminder Frequency
              </label>
              <select
                value={preferences.reminderFrequency}
                onChange={(e) => setPreferences({
                  ...preferences,
                  reminderFrequency: e.target.value as 'DAILY' | 'WEEKLY' | 'MONTHLY'
                })}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="DAILY">Daily</option>
                <option value="WEEKLY">Weekly</option>
                <option value="MONTHLY">Monthly</option>
              </select>
            </div>

            <div>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={preferences.escalationEnabled}
                  onChange={(e) => setPreferences({
                    ...preferences,
                    escalationEnabled: e.target.checked
                  })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <Bell className="w-5 h-5 text-gray-600" />
                <span>Enable escalation</span>
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-500 mb-4">Unable to load preferences</div>
            <button
              onClick={loadPreferences}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg"
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default ReminderPreferences 